import createClient, { type Middleware } from "openapi-fetch";

import type { paths } from "@/generated/schema";

import { ApiError, toApiError, unwrapEnvelope } from "./errors";

export type { paths };

/** Hooks the shell installs at startup (keeps api/ free of shell imports). */
type ApiHooks = {
  getToken: () => string | null;
  /** Called on `401 UNAUTHORIZED` from an authenticated route: the session is gone. */
  onSessionExpired: () => void;
};

let hooks: ApiHooks = { getToken: () => null, onSessionExpired: () => {} };

export function configureApi(next: ApiHooks): void {
  hooks = next;
}

// Server clock skew from the last `Date` header, for "tomorrow UTC" (spec §7).
let serverSkewMs = 0;
export const serverNow = (): Date => new Date(Date.now() + serverSkewMs);

function recordServerDate(res: Response): void {
  const header = res.headers.get("Date");
  const t = header ? Date.parse(header) : NaN;
  if (!Number.isNaN(t)) serverSkewMs = t - Date.now();
}

const PUBLIC_AUTH = /\/api\/v1\/auth\/(login|password-reset)/;

async function handleUnauthorized(req: Request, res: Response): Promise<void> {
  if (res.status !== 401 || PUBLIC_AUTH.test(req.url)) return;
  // Only the session middleware's UNAUTHORIZED means "session gone". Handlers on authenticated routes also
  // answer 401 for a bad input (PATCH me wrong current password, 2fa/enable wrong code) and must keep it.
  const body: unknown = await res
    .clone()
    .json()
    .catch(() => null);
  const code = (body as { error?: { code?: string } } | null)?.error?.code;
  if (!code || code === "UNAUTHORIZED") hooks.onSessionExpired();
}

const middleware: Middleware = {
  onRequest({ request }) {
    const token = hooks.getToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
  async onResponse({ request, response }) {
    recordServerDate(response);
    await handleUnauthorized(request, response);
    // openapi-fetch only skips JSON parsing for 204 / Content-Length 0; 202 bodies are empty too.
    if (response.status === 202 || response.status === 204) {
      return new Response(null, { status: 204, headers: response.headers });
    }
    return response;
  },
};

/** Typed openapi-fetch client. Use it through `unwrap` so the envelope and errors are handled. */
export const client = createClient<paths>({ baseUrl: "" });
client.use(middleware);

type FetchResult = { data?: unknown; error?: unknown; response: Response };
type Inner<D> = D extends { data?: infer X } ? X : never;
type OrUndefined<X> = [X] extends [never] ? undefined : X;
/** `data` inside the success envelope, from the schema; `undefined` for routes without a body (202/204). */
type DataOf<R> = OrUndefined<R extends { data?: infer D } ? NonNullable<Inner<NonNullable<D>>> : never>;

/**
 * Awaits an openapi-fetch call and returns the envelope's `data`, or throws `ApiError`.
 * Data type comes from the OpenAPI schema; pass `unwrap<T>(…)` where the schema has none (passthroughs).
 */
export function unwrap<R extends FetchResult>(call: Promise<R>): Promise<DataOf<R>>;
export function unwrap<T>(call: Promise<FetchResult>): Promise<T>;
export async function unwrap(call: Promise<FetchResult>): Promise<unknown> {
  let result: FetchResult;
  try {
    result = await call;
  } catch (e) {
    throw new ApiError(0, "NETWORK", e instanceof Error ? e.message : "Network error");
  }
  const { response } = result;
  return unwrapEnvelope(response.status, response.ok ? result.data : result.error);
}

/**
 * Downloads a CSV (or any attachment) endpoint as a file; never parses it (spec §2.1).
 * `path` is a same-origin API path, `query` its parameters (e.g. `{format: "csv"}`).
 */
export async function downloadFile(path: string, query: Record<string, string | undefined> = {}): Promise<void> {
  const qs = new URLSearchParams(Object.entries(query).filter((e): e is [string, string] => e[1] !== undefined));
  const token = hooks.getToken();
  const req = new Request(`${path}?${qs}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  const res = await fetch(req);
  if (!res.ok) {
    await handleUnauthorized(req, res);
    const text = await res.text();
    let body: unknown = text;
    try {
      body = JSON.parse(text);
    } catch {}
    throw toApiError(res.status, body);
  }
  const name = /filename="?([^";]+)"?/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "export.csv";
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
